import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { getCurrentStoreId } from '../../utils/apiService';
import {
  currentPosSystemForOrdersCard,
  fetchIntegrationsStoreInfo,
  fetchOrdersIntegrationStatus,
  getPosInfo,
  ordersStatusHasActiveConnection,
  startPosConnect,
} from './integrationsModel';

/**
 * The parts of Integrations that Getting started shows in its own steps:
 * the ordering-system connection and the OpenTable instructions. The
 * Integrations page and the steps read the same queries.
 */

/** The store's ordering system, whether it's connected, and its OAuth start. */
export function useOrdersIntegration() {
  const { user } = useAuth();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const storeInfo = useQuery({
    queryKey: useScopedQueryKey('integrations-store-info'),
    queryFn: () =>
      fetchIntegrationsStoreInfo(
        getCurrentStoreId() ||
          (user?.store_id != null ? String(user.store_id) : null),
      ),
  });
  const status = useQuery({
    queryKey: useScopedQueryKey('orders-integration-status'),
    queryFn: fetchOrdersIntegrationStatus,
  });

  const system = currentPosSystemForOrdersCard(
    status.data ?? null,
    storeInfo.data ?? null,
  );
  const key = system ? system.toUpperCase() : '';
  const posInfo = key && key !== 'UNKNOWN' ? getPosInfo(key) : null;

  /** Sends the browser to the POS's OAuth page, or sets `error`. */
  const connect = async () => {
    setConnecting(true);
    setError('');
    try {
      const result = await startPosConnect();
      if (result && 'redirectUrl' in result) {
        window.location.href = result.redirectUrl;
        return;
      }
      if (result) setError(result.error);
    } catch {
      setError('Network error — please try again.');
    } finally {
      setConnecting(false);
    }
  };

  return {
    posInfo,
    connected: ordersStatusHasActiveConnection(status.data ?? null),
    loading: storeInfo.isPending || status.isPending,
    connect,
    connecting,
    error,
  };
}

export const openGuestCenter = () =>
  window.open(
    'https://guestcenter.opentable.com/',
    '_blank',
    'noopener,noreferrer',
  );

/** Where Ask Arthur is switched on inside OpenTable. */
export const OpenTablePath: React.FC<{ className?: string }> = ({
  className,
}) => (
  <p className={className ?? 'text-v2-13 text-v2-fg'}>
    <span className="text-v2-fg-3">Go to</span> OpenTable Guest Center{' '}
    <span className="text-v2-fg-3">&gt;</span> Integrations{' '}
    <span className="text-v2-fg-3">&gt;</span> Phone Automation{' '}
    <span className="text-v2-fg-3">&gt;</span> Ask Arthur
  </p>
);
